import { useState } from 'react';
import { Stack } from '@mui/material';
import { useRecoilValue } from 'recoil';
import {
  ExpandableContentButton,
  ExpandableContentWrapper,
} from 'components/shared';
import { CanvasGroupLinkButton } from 'components/canvas/groups';
import { PutCallImpactGraph } from 'components/equityhub/EquityGraph/PutCallImpactGraph';
import { TotalOIPutCallImpactProvider } from 'components/equityhub/EquityGraph/TotalOIPutCallImpactContext';
import { TotalOIPutCallImpactZoomOutButton } from 'components/equityhub/EquityGraph/TotalOIPutCallImpactZoomOutButton';
import { useTotalOIPutCallImpactCtx } from 'components/equityhub/EquityGraph/useTotalOIPutCallImpactCtx';
import { FormControlLabel, Switch, Typography } from '@mui/material';
import { equitySymbolsState } from 'states';
import { CanvasContentId } from 'types/canvas';
import { CanvasComponentConfig } from 'types/canvas';
import {
  TotalOIPutCallImpactConfig,
  DEFAULT_TOTAL_OI_PC_IMPACT_CONFIG,
} from 'types';
import CanvasSymSelector from './CanvasSymSelector';

interface CanvasTotalOIPutCallImpactProps {
  id: number;
  sym: string;
  config: CanvasComponentConfig;
  onSymChange: (sym: string) => void;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
}

const KeyLevelsToggle = () => {
  const { config, updateConfig } = useTotalOIPutCallImpactCtx();
  return (
    <FormControlLabel
      className="canvas-no-drag"
      control={
        <Switch
          size="small"
          checked={config.showKeyLevels}
          onChange={(_e, checked) => updateConfig({ showKeyLevels: checked })}
        />
      }
      label={<Typography sx={{ fontSize: 12 }}>Key Levels</Typography>}
      labelPlacement="start"
      sx={{ mx: 0 }}
    />
  );
};

const CanvasTotalOIPutCallImpact = ({
  id,
  sym,
  config: canvasConfig,
  onSymChange,
  onConfigChange,
}: CanvasTotalOIPutCallImpactProps) => {
  const ehSymbols = useRecoilValue(equitySymbolsState);
  const symOptions = ehSymbols.map((s) => ({ symbol: s.symbol, name: s.name }));

  const [localConfig, setLocalConfig] = useState<TotalOIPutCallImpactConfig>(
    () => ({
      ...DEFAULT_TOTAL_OI_PC_IMPACT_CONFIG,
      ...(canvasConfig as Partial<TotalOIPutCallImpactConfig>),
    }),
  );

  const handleConfigChange = (patch: Partial<TotalOIPutCallImpactConfig>) => {
    setLocalConfig((prev) => {
      const next = { ...prev, ...patch };
      onConfigChange(patch);
      return next;
    });
  };

  const expandableContentButtonType = `${CanvasContentId.TOTAL_OI_PUT_CALL_IMPACT_CHART}-${id}`;

  return (
    <TotalOIPutCallImpactProvider
      selectedSym={sym}
      config={localConfig}
      onConfigChange={handleConfigChange}
    >
      <ExpandableContentWrapper
        type={expandableContentButtonType}
        styleOverrides={{ p: 1, height: '100%' }}
      >
        <Stack
          direction="column"
          spacing={1}
          sx={{ height: '100%', width: '100%' }}
        >
          <Stack
            sx={{
              px: 1,
              py: 0.5,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <CanvasSymSelector
              sym={sym}
              options={symOptions}
              onSymChange={onSymChange}
            />
            <TotalOIPutCallImpactZoomOutButton />
            <KeyLevelsToggle />
            <CanvasGroupLinkButton
              componentId={id}
              contentId={CanvasContentId.TOTAL_OI_PUT_CALL_IMPACT_CHART}
            />
            <ExpandableContentButton type={expandableContentButtonType} />
          </Stack>
          <PutCallImpactGraph />
        </Stack>
      </ExpandableContentWrapper>
    </TotalOIPutCallImpactProvider>
  );
};

export default CanvasTotalOIPutCallImpact;
